import moment from 'moment-timezone';
import { startCase, isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { Loader, Message } from 'semantic-ui-react';
import md5 from 'md5';
import ErrorMessage from '../ErrorMessage';
import './DealList.scss';

const DEFAULT_STATUS_NAME = 'Customer Info';

const getStatusName = (status) => {
  if (!status) return undefined;

  switch (status) {
    case 'CarCardList':
    case 'CarDetails':
    case 'CarFilter':
      return 'Car Search';
    case 'CarPaymentBuilder':
    case 'PaymentComparison':
      return 'Ownership Options';
    case 'CarTestDrive':
      return 'Test Drive';
    case 'ExistingTradeInList':
    case 'TradeInContainer':
    case 'VinCodeScan':
      return 'Trade-In';
    case 'ApplicantFinal':
    case 'CoapplicantFinal':
    case 'ApplicantCreditInfo':
    case 'CoapplicantCreditInfo':
      return 'Credit Application';
    case 'FormTemplateView':
    case 'FormLibraryFinal':
      return 'Forms';
    case 'DocumentCardScan':
      return 'Document Scan';
    default:
      return startCase(status);
  }
};

export default function DealList({
  dealer,
  list,
  showHiddenDeals,
  hiddenDeals = [],
  setHiddenDeals,
  error,
  timeZone,
  onlineOnly,
  view,
}) {
  const isHidden = useCallback(
    (deal) => hiddenDeals.includes(md5(deal.id)),
    [hiddenDeals],
  );

  const [showFilters, setShowFilters] = useState(false);
  const [customerName, setCustomerName] = useState('');

  const visibleList = useMemo(() => {
    const dealIsVisible = (deal) => showHiddenDeals || !isHidden(deal);
    const isCustomerName = (deal) => {
      const firstName = deal.applicant?.firstName || '';
      const lastName = deal.applicant?.lastName || '';
      const fullName = [firstName, ' ', lastName].join('');
      return (
        !isNil(fullName) &&
        fullName.toLowerCase().includes(customerName.toLowerCase())
      );
    };
    return list && list.filter(dealIsVisible).filter(isCustomerName);
  }, [showHiddenDeals, isHidden, customerName, list]);

  const renderFilters = () => (
    <div className="filters-container">
      {/* eslint-disable-next-line */}
      <a href="#" onClick={() => setShowFilters(!showFilters)}>
        {showFilters ? 'Hide ' : 'Show '} Search Filters
      </a>
      <div
        className={`deals-filters-form-container ${showFilters ? 'show' : ''}`}
      >
        <div className="form-group col-3">
          <label htmlFor="customerName">Customer Name</label>
          <input
            type="text"
            className="form-control amp-mask"
            name="customerName"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            data-dd-privacy="mask"
          />
        </div>
        <div className="form-group col-3">
          <input
            type="submit"
            value="Clear Filters"
            className="btn filter-button amp-mask"
            onClick={() => setCustomerName('')}
            data-dd-privacy="mask"
          />
        </div>
      </div>
    </div>
  );

  const renderRow = (deal, key) => {
    const { applicant, updatedAt, carName } = deal;
    const dealStatus = getStatusName(deal?.progress) || DEFAULT_STATUS_NAME;

    let applicantName = '-';
    if (applicant && applicant.firstName && applicant.lastName) {
      applicantName = `${applicant.firstName} ${applicant.lastName}`;
    }

    let updatedString = '-';
    if (updatedAt) {
      if (timeZone) {
        // t
        moment.tz.setDefault(timeZone);
      }
      updatedString = moment(updatedAt).format('YYYY/MM/DD h:mm A');
    }

    const isDealHidden = isHidden(deal);
    const toggleHidden = () => {
      const hashedDealId = md5(deal.id);
      if (isDealHidden) {
        setHiddenDeals(hiddenDeals.filter((id) => id !== hashedDealId));
      } else {
        setHiddenDeals([...hiddenDeals, hashedDealId]);
      }
    };

    const { source } = deal;

    return (
      <tr key={key}>
        {onlineOnly ? null : (
          <td data-dd-privacy="mask" className="amp-mask">
            {deal.userFirstName} {deal.userLastName}
          </td>
        )}
        <td>{dealStatus}</td>
        {onlineOnly ? null : <td>{source || '-'}</td>}
        <td data-dd-privacy="mask" className="amp-mask">
          {applicantName}
        </td>
        <td>{carName || '-'}</td>
        <td>{updatedString}</td>
        {showHiddenDeals && (
          <td>
            <button
              className={
                isDealHidden ? 'deal-list-show-deal' : 'deal-list-hide-deal'
              }
              onClick={toggleHidden}
            >
              {isDealHidden ? 'Unhide' : 'Hide'}
            </button>
          </td>
        )}
      </tr>
    );
  };

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="deal-list-container">
      {renderFilters()}
      <div className="deal-list-table-container">
        {!visibleList && <Loader active inline="centered" />}
        {visibleList && visibleList.length === 0 && (
          <Message>
            <Message.Header>
              There are no deals for this time frame.
            </Message.Header>
          </Message>
        )}
        {visibleList && visibleList.length >= 1 && (
          <table className="deal-list-table">
            <thead>
              <tr>
                {onlineOnly ? null : <th>Salesperson</th>}
                <th>Status</th>
                {onlineOnly ? null : <th>Type</th>}
                <th>Customer</th>
                <th>Vehicle</th>
                <th>Time updated</th>
              </tr>
            </thead>
            <tbody>{visibleList.map(renderRow)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

DealList.propTypes = {
  dealer: PropTypes.object,
  error: PropTypes.any,
  list: PropTypes.array,
  onlineOnly: PropTypes.bool,
  showHiddenDeals: PropTypes.bool,
  hiddenDeals: PropTypes.array,
  setHiddenDeals: PropTypes.func,
  timeZone: PropTypes.string,
  view: PropTypes.string,
  // timeZone is purely for automated testing purposes as the different in the test timezone cases issues.
};
